import PropTypes from 'prop-types';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import {Subject} from 'rxjs';
import {debounceTime, filter} from 'rxjs/operators';

import {translate} from 'iss/strings';
import styles from 'iss/styles/SearchableSelector.styl';
import {toObservable} from 'iss/utils/subscriptionUtils';
import {Button} from 'iss/widgets/Buttons';
import {FlexItem, FlexRow} from 'iss/widgets/Flex';
import {LoadingIndicator} from 'iss/widgets/LoadingIndicator';
import {RestResultMessage} from 'iss/widgets/MessageBox';
import {TextSearchBox} from 'iss/widgets/bootstrap/SearchBox';

export {TextSearchBox} from 'iss/widgets/bootstrap/SearchBox';

const EMPTY_STATE = {
  searchResult: {
    results: [],
    totalResults: 0,
  },
  error: null,
  loading: true,
  pagesLoaded: 0,
};

/** A helper class that provides a search box and automatic paging
 * functionality when provided with a getItems callback prop.
 *
 * Props:
 *
 * search :: string
 * The search string or data to use. Used to refresh the component on search string changes
 *
 * getItems(searchStr, pageNr, PageSize) :: string -> number -> number -> SearchResult | Observable<SearchResult>
 * The function used to fetch the items to show
 * The SearchResult object should have the fields results :: Array<object>, totalResults :: number
 *
 * children :: SearchResult -> PagedSearchResultsApi -> ReactElement
 * A function that is given the items to show and returns their dom representation
 *
 * PagedSearchResultsApi:
 *
 * reloadResults() :: void
 * Reloads the current result set of search results from server
 *
 * loadAllItems() :: void
 * Loads all the missing results not currently loaded into the view
 *
 * loadMoreItems() :: void
 * Loads one more page of results
 *
 **/
export class PagedSearchResults extends React.Component {
  static propTypes = {
    getItems: PropTypes.func.isRequired,
    search: PropTypes.any.isRequired,
    children: PropTypes.func.isRequired,
  };
  static defaultProps = {
    debounced: true,
    errorComponent: ({error}) => <RestResultMessage id="loadingError" result={error} />,
    pageSize: 30,
  };
  constructor(props) {
    super(props);
    this.state = EMPTY_STATE;
    this.reloadEventually = new Subject();
    this.reloadEventually.pipe(debounceTime(350)).subscribe(this.reload);
    this.requestNo = 0;
  }
  componentDidMount() {
    this.loadMoreItems();
  }
  componentDidUpdate(prevProps) {
    const {search, pageSize, debounced}= this.props;
    if (
      prevProps.search === search &&
      prevProps.pageSize == pageSize
    ) return;
    if (debounced) {
      this.reloadEventually.next();
    } else {
      this.reload();
    }
  }
  reload = () => {
    this.setState(EMPTY_STATE, this.loadMoreItems);
  };
  loadMoreItems = () => {
    const existingItems = this.state.searchResult.results;
    this.loadItems(this.state.pagesLoaded, this.props.pageSize)
      .subscribe(({results, totalResults}) => {
        this.setResults({
          results: [...existingItems, ...results],
          totalResults
        });
      }, this.setError);
  };
  loadAllItems = () => {
    this.loadItems(0, 10000).subscribe(this.setResults, this.setError);
  };
  loadItems = (page, size) => {
    const {search, getItems} = this.props;
    const requestNo = ++this.requestNo;
    return toObservable(getItems(search, page, size))
      // Ignore responses to old requests (local-scoped requestNo < this.requestNo):
      .pipe(filter(() => this.requestNo == requestNo));
  };
  setResults = ({results, totalResults})=> {
    const {pageSize} = this.props;
    this.setState({
      searchResult: { results, totalResults },
      error: null,
      loading: false,
      pagesLoaded: Math.ceil(results.length / pageSize)
    });
  };
  setError = (err) => {
    this.setState({
      ...EMPTY_STATE,
      error: err,
      loading: false,
    });
  };
  api = {
    loadMoreItems: this.loadMoreItems,
    loadAllItems: this.loadAllItems,
    reload: this.reload,
  };
  render() {
    const {children, errorComponent} = this.props;
    const [ErrorComponent] = [errorComponent];
    const {searchResult, error, loading} = this.state;

    // TODO display indicator only after a delay to avoid displaying it if the request completes quickly (annoying flickerit it causes).
    //      i.e. similarly as in RequestLoadingIndicator, or perhaps only use RequestLoadingIndicator? Now there seems to be N different
    //      places where showLoadingScreen is false and thus RequestLoadingIndicator isn't used :(
    if (loading) return <LoadingIndicator isLoading={loading} />;
    if (error) return <ErrorComponent error={error}/>;

    return children(searchResult, this.api);
  }
}

/** An InfiniteScroll wrapper that takes searchResults and a load more callback
 * and calls the load more callback if the user scrolls past current results
 * and there are more results to load
 *
 * Optionally takes a hasMaxHeight parameter that can be used for containers
 * that scroll items inside them and have a maximum height. You need to
 * provide the relevant styles yourself to the children if you do this.
 * See SearchableSelector for an example of it.
 */
export function SearchResultScroller({hasMaxHeight = false, searchResult, children, onLoadMore, ...props}) {
  const infiniteScroll = <InfiniteScroll pageStart={0} loadMore={onLoadMore}
    hasMore={searchResult.results.length < searchResult.totalResults}
    loader={<p key={0}>...</p>} useWindow={!hasMaxHeight}
  >
    {children}
  </InfiniteScroll>;

  // The extra div is essential for the InfiniteScroll
  // component to work when a maxHeight is used.
  return hasMaxHeight ?
    <div {...props}>{infiniteScroll}</div> :
    infiniteScroll;
}

/** Shows a "Show all results" button if there are more results to load.
 *
 * By default does not show the button if loading all results would
 * show more than 200 entries. This is to prevent performance problems
 */
export function ShowAllResultsButton({searchResult, limit=200, onShowAll}) {
  const totalItems = searchResult.totalResults;
  const hasMoreItems = totalItems > searchResult.results.length;

  if (!hasMoreItems) return null;
  if (totalItems > limit) return null;

  return <Button onClick={onShowAll} text={translate('common.showAllResults')} />;
}

export function formatResultCount({results, totalResults}) {
  const allShown = totalResults == results.length;
  return allShown ?
    translate("common.searchResultsCount", {count: results.length}) :
    translate("common.searchResultsShown", {count: results.length, total: totalResults});
}


/* TextSearchBox with optional buttons or other controls alongside it */
export function TextSearchBar({children, className, ...props}) {
  if (!children) return <TextSearchBox {...props} />;

  return <FlexRow className={styles.search + (className ? " " + className : "")}>
    <FlexItem>
      <TextSearchBox className="" {...props} />
    </FlexItem>
    <FlexItem flex="none">
      {children}
    </FlexItem>
  </FlexRow>;
}

export default PagedSearchResults;



// WEBPACK FOOTER //
// ./modules/issFrontend/widgets/PagedSearchResults.jsx